/* A month at a glance: which days have something to hear.
 *
 * Modelled on a surveillance-camera app's playback calendar, which is the right
 * shape for "I know roughly when it happened" -- the question an operator
 * actually arrives with. A flat list answers "what is the most recent thing",
 * which is a different and rarer question.
 *
 * What a dot means is decided by the server (`/calendar`) and is deliberately
 * NOT "there is archive audio". On this fleet there is none and never will be:
 * `audio` uploads only at `full` tier and `events` is the default, so a
 * calendar keyed on it would show an empty month for every mission for ever. A
 * dot means a clip someone saved, or audio the node is holding on its card.
 */
import { useMemo } from 'react'

export interface CalendarDay {
  day: string
  clips: number
  clip_seconds: number
  node_seconds: number
  node_uploaded_seconds: number
  feature_hours: number
}

/** Monday first. This is a Norwegian operation and a calendar that starts on
 *  Sunday is read wrong at a glance, which is the only way a calendar is
 *  read. */
const WEEKDAYS = ['ma', 'ti', 'on', 'to', 'fr', 'lø', 'sø']

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December']

function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function hours(seconds: number): string {
  if (!seconds) return ''
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  return `${(seconds / 3600).toFixed(1)} h`
}

/** What this day holds, as a sentence. Used for the tooltip and, more
 *  importantly, as the cell's accessible name -- a coloured dot conveys
 *  nothing to a screen reader, and "has data" is the whole content here. */
function describe(d: CalendarDay | undefined, iso: string): string {
  if (!d) return `${iso}: nothing recorded`
  const parts: string[] = []
  if (d.clips) parts.push(`${d.clips} clip${d.clips === 1 ? '' : 's'} in the archive`)
  if (d.node_seconds) parts.push(`${hours(d.node_seconds)} on the node`)
  if (!parts.length && d.feature_hours) parts.push(`${d.feature_hours} h of measurements, no audio`)
  return parts.length ? `${iso}: ${parts.join(', ')}` : `${iso}: nothing recorded`
}

export function Calendar({
  month, days, selected, onMonth, onSelect, uploadsAudio,
}: {
  /** YYYY-MM */
  month: string
  days: CalendarDay[]
  /** YYYY-MM-DD, or null */
  selected: string | null
  onMonth: (month: string) => void
  onSelect: (day: string) => void
  /** False on every events-tier node. Changes what "on the node" means: a
   *  permanent intended state rather than an upload that has not happened. */
  uploadsAudio: boolean
}) {
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarDay>()
    for (const d of days) m.set(d.day.slice(0, 10), d)
    return m
  }, [days])

  const [y, m] = [Number(month.slice(0, 4)), Number(month.slice(5, 7))]

  // Built in UTC throughout. The archive is indexed in UTC and a calendar that
  // silently shifts by the viewer's timezone would put a clip on the wrong day
  // for anyone east of Greenwich -- which is everyone here.
  const cells = useMemo(() => {
    const firstOfMonth = new Date(Date.UTC(y, m - 1, 1))
    // getUTCDay is 0=Sunday; shift so Monday is 0.
    const lead = (firstOfMonth.getUTCDay() + 6) % 7
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
    const out: (string | null)[] = Array(lead).fill(null)
    for (let i = 1; i <= daysInMonth; i++) out.push(ymd(new Date(Date.UTC(y, m - 1, i))))
    while (out.length % 7) out.push(null)
    return out
  }, [y, m])

  const shift = (by: number) => {
    const d = new Date(Date.UTC(y, m - 1 + by, 1))
    onMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }

  const today = ymd(new Date())
  const anything = days.some((d) => d.clips || d.node_seconds)

  return (
    <div className="cal">
      <div className="cal-head">
        {/* Navigation, not actions. Ghost rather than primary: the one call to
            action on this page belongs to the clip you open, and a month arrow
            competing with it would make neither read as the thing to press. */}
        <button className="btn ghost" onClick={() => shift(-1)}
                aria-label="Previous month">‹</button>
        <span className="num" aria-live="polite">{MONTHS[m - 1]} {y}</span>
        <button className="btn ghost" onClick={() => shift(1)}
                aria-label="Next month">›</button>
      </div>

      <div className="cal-grid" role="grid" aria-label={`${MONTHS[m - 1]} ${y}`}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="cal-wd" aria-hidden="true">{w}</div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`x${i}`} className="cal-cell empty" />
          const d = byDay.get(iso)
          const has = !!(d && (d.clips || d.node_seconds))
          return (
            <button
              key={iso}
              role="gridcell"
              className={[
                'cal-cell',
                has ? 'has' : '',
                iso === selected ? 'sel' : '',
                iso === today ? 'today' : '',
              ].filter(Boolean).join(' ')}
              aria-selected={iso === selected}
              aria-label={describe(d, iso)}
              title={describe(d, iso)}
              onClick={() => onSelect(iso)}
            >
              <span className="cal-n">{Number(iso.slice(8))}</span>
              {/* Two marks, because they mean different things and merging
                  them would hide the distinction the calendar exists to show.
                  Filled: a clip is in object storage. Hollow: audio is on the
                  node and nowhere else. */}
              <span className="cal-dots" aria-hidden="true">
                {d && d.clips > 0 && <i className="dot solid" />}
                {d && d.node_seconds > 0 && d.clips === 0 && <i className="dot hollow" />}
              </span>
            </button>
          )
        })}
      </div>

      {!anything && (
        <p className="faint" style={{ margin: '10px 2px 2px' }}>
          Nothing recorded this month. Clips saved while listening appear here,
          and so does audio the node is still holding.
        </p>
      )}

      <p className="faint cal-key">
        <i className="dot solid" /> clip in the archive
        <i className="dot hollow" style={{ marginLeft: 14 }} />{' '}
        {uploadsAudio
          ? 'on the node, not yet uploaded'
          : 'on the node — this tier keeps audio locally by design'}
      </p>
    </div>
  )
}
